import { z } from 'zod'
import { emailSchema, tagsSchema, notesSchema, createQuerySchema } from '@repo/core'

/**
 * 🏢 Client Company (Multi-Tenant Portfolio)
 */
export interface ClientCompany {
  id: string
  tenantId: string
  companyName: string
  tinNumber: string // Philippines Tax Identification Number (9 or 12 digits)
  birBranchCode: string // e.g. "0000"
  rdoCode: string // Revenue District Office e.g. "047"
  industry: string
  contactPerson: string
  contactEmail: string
  contactPhone: string
  assignedPartnerId: string
  assignedAccountantId: string
  status: 'active' | 'onboarding' | 'suspended' | 'archived'
  monthlyRetainerAmount: number // in PHP ₱
  createdAt: number
  updatedAt: number
  deletedAt: number | null
  version: number
  createdBy: string
  updatedBy: string
}

export const CreateClientCompanySchema = z.object({
  tenantId: z.string().min(1),
  companyName: z.string().min(1).max(250),
  tinNumber: z.string().min(9).max(15),
  birBranchCode: z.string().default('0000'),
  rdoCode: z.string().min(2).max(10),
  industry: z.string().min(1),
  contactPerson: z.string().min(1),
  contactEmail: emailSchema,
  contactPhone: z.string().min(5),
  assignedPartnerId: z.string().min(1),
  assignedAccountantId: z.string().min(1),
  status: z.enum(['active', 'onboarding', 'suspended', 'archived']).default('active'),
  monthlyRetainerAmount: z.number().nonnegative(),
})

/**
 * 📁 Client Document Vault
 */
export interface ClientDocumentVaultItem {
  id: string
  clientId: string
  documentName: string
  category: 'tax_return' | 'receipt' | 'financial_statement' | 'bir_compliance' | 'other'
  fileUrl: string
  fileSizeKb: number
  uploadedByRole: 'client' | 'accountant' | 'partner'
  uploadedByName: string
  status: 'pending_review' | 'approved' | 'rejected'
  uploadedAt: number
}

/**
 * ⏱️ Timesheet & Billable Hours
 */
export interface TimesheetEntry {
  id: string
  accountantId: string
  accountantName: string
  clientId: string
  clientName: string
  taskDescription: string
  hoursSpent: number
  hourlyRate: number // in PHP ₱
  billableAmount: number // hoursSpent * hourlyRate
  isBilled: boolean
  invoiceId?: string
  date: string // YYYY-MM-DD
}

/**
 * 💳 Billing Invoice & Weekly Collections
 */
export interface BillingInvoice {
  id: string
  invoiceNumber: string
  clientId: string
  clientName: string
  period: string
  items: Array<{ description: string; amount: number }>
  totalAmount: number // in PHP ₱
  status: 'draft' | 'sent' | 'partially_paid' | 'paid' | 'overdue'
  dueDate: string
  paidDate?: string
  weeklyCollectionWeek: string // YYYY-Www
}

/**
 * 💵 Petty Cash Voucher (< ₱5,000 Threshold Alert Logic)
 */
export interface PettyCashVoucher {
  id: string
  voucherNumber: string
  requestedById: string
  requestedByName: string
  amount: number // in PHP ₱
  purpose: string
  receiptPhotoUrl?: string
  category: 'transportation' | 'office_supplies' | 'client_meeting' | 'bir_filing_fee' | 'other'
  status: 'pending_approval' | 'approved' | 'disbursed' | 'rejected'
  approvedById?: string
  approvedByName?: string
  requestedAt: number
  disbursedAt?: number
}

export const CreatePettyCashVoucherSchema = z.object({
  amount: z.number().positive(),
  purpose: z.string().min(3).max(500),
  category: z.enum(['transportation', 'office_supplies', 'client_meeting', 'bir_filing_fee', 'other']),
  receiptPhotoUrl: z.string().optional(),
})

/**
 * 🇵🇭 BIR Alphalist Filing (Forms 1604-C, 1604-F, 1604-E)
 */
export interface AlphalistFiling {
  id: string
  clientId: string
  clientName: string
  formType: '1604-C' | '1604-F' | '1604-E'
  taxableYear: number
  totalTaxWithheld: number // in PHP ₱
  totalEmployeesOrPayees: number
  missingTinCount: number
  generatedDatFileName: string
  status: 'draft' | 'validated' | 'submitted_to_bir' | 'rejected'
  datFileContent: string
  createdAt: number
}

/**
 * 🤖 Automation Engine Rule (IF / THEN Triggers)
 */
export interface AutomationRule {
  id: string
  name: string
  triggerEvent: 'recurring_date_1st' | 'tax_deadline_minus_5d' | 'petty_cash_below_5k' | 'weekly_collections_friday_5pm'
  automatedAction: string
  benefit: string
  isActive: boolean
  lastTriggeredAt?: number
}

export const LedgerSyncQuerySchema = createQuerySchema({
  status: z.string().optional(),
  formType: z.string().optional(),
})
